import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/authContext.jsx';
import { useAbrirObra } from '../hooks/useAbrirObra.js';
import { initiateSocketConnection } from '../service/socketio.js';
import { Modal } from '../components/ui/Modal.jsx';

const API = '/api';
const ROLES_SEGUIMIENTO = ['seguimiento_obras', 'admin'];
const POR_PAGINA = 25;

const fechaDia = (dia) => {
    if (!dia) return '—';
    const [y, m, d] = String(dia).slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}/${y}` : '—';
};

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR') : '—');

const transcurridos = (n) => (n == null ? '—' : `${n} días`);
const restantes = (n) => {
    if (n == null) return '—';
    if (n > 0) return `faltan ${n} día${n === 1 ? '' : 's'}`;
    if (n === 0) return 'vence hoy';
    return `vencido hace ${-n} día${-n === 1 ? '' : 's'}`;
};

const ETIQUETA_HITO = {
    inicial: 'Notificación inicial',
    recordatorio: 'Recordatorio',
    notificacion: 'Notificación con plazo',
    pv: 'Confeccionar PV',
};

// Color por ciclo (1 amarillo, 2 naranja, 3 rojo, 4+ violeta).
const COLOR_CICLO = {
    amarillo: 'bg-amber-100 text-amber-800 border-amber-400 dark:bg-amber-900/30 dark:text-amber-300',
    naranja: 'bg-orange-100 text-orange-800 border-orange-400 dark:bg-orange-900/30 dark:text-orange-300',
    rojo: 'bg-red-100 text-red-800 border-red-400 dark:bg-red-900/30 dark:text-red-300',
    violeta: 'bg-violet-100 text-violet-800 border-violet-400 dark:bg-violet-900/30 dark:text-violet-300',
};
const colorBadge = (color) => COLOR_CICLO[color] || COLOR_CICLO.violeta;

// Días desde la base hasta el aviso de cada ciclo.
const ETIQUETA_SITUACION = {
    pendiente: 'Pendiente',
    en_plazo: 'En Plazo',
    resuelta: 'Resuelta',
    sin_notificacion: 'Sin notificación',
};

export default function AlertasObrasPage() {
    const { token, role, roles } = useContext(AuthContext);
    const location = useLocation();
    const { abrirObra } = useAbrirObra('alertas_obras');

    const puedeVer = useMemo(() => {
        if (Array.isArray(roles) && roles.some((r) => ROLES_SEGUIMIENTO.includes(r))) return true;
        return ROLES_SEGUIMIENTO.includes(role);
    }, [role, roles]);

    const params = new URLSearchParams(window.location.search);

    const [ventanas, setVentanas] = useState([]);
    const [total, setTotal] = useState(0);
    const [obrasTotal, setObrasTotal] = useState(0);
    const [pendientesTotal, setPendientesTotal] = useState(0);
    const [enPlazoTotal, setEnPlazoTotal] = useState(0);
    const [resueltasTotal, setResueltasTotal] = useState(0);
    const [sinNotificacionTotal, setSinNotificacionTotal] = useState(0);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [situacion, setSituacion] = useState('');
    const [obra, setObra] = useState(params.get('obra') || '');
    const [pagina, setPagina] = useState(0);
    const [abierto, setAbierto] = useState(null);

    const [modal, setModal] = useState(null); // { tipo:'notificacion'|'pv', ventana, hito }
    const [draft, setDraft] = useState(null);
    const [cargandoDraft, setCargandoDraft] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [mensajeModal, setMensajeModal] = useState('');

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const cargar = async ({ silencioso = false, obraOverride, situacionOverride } = {}) => {
        if (!token) return;
        if (!silencioso) setCargando(true);
        setError('');
        try {
            const qs = new URLSearchParams();
            const sitValor = situacionOverride !== undefined ? situacionOverride : situacion;
            if (sitValor) qs.set('situacion', sitValor);
            const obraValor = obraOverride !== undefined ? obraOverride : obra;
            if (obraValor && obraValor.trim()) qs.set('obra', obraValor.trim());
            qs.set('limite', POR_PAGINA);
            qs.set('offset', pagina * POR_PAGINA);
            const res = await fetch(`${API}/alertas-obras/seguimiento?${qs.toString()}`, { headers, cache: 'no-store' });
            if (!res.ok) throw new Error('Error al cargar el seguimiento de alertas de obras');
            const json = await res.json();
            setVentanas(json?.data?.ventanas || []);
            setTotal(json?.data?.total || 0);
            setObrasTotal(json?.data?.obrasTotal || 0);
            setPendientesTotal(json?.data?.pendientesTotal || 0);
            setEnPlazoTotal(json?.data?.enPlazoTotal || 0);
            setResueltasTotal(json?.data?.resueltasTotal || 0);
            setSinNotificacionTotal(json?.data?.sinNotificacionTotal || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            if (!silencioso) setCargando(false);
        }
    };

    const cargarRef = useRef(null);
    cargarRef.current = cargar;

    useEffect(() => {
        if (!puedeVer) return;
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [puedeVer, token, situacion, pagina]);

    // Sincroniza el filtro con ?obra= de la URL: cuando la campana navega a
    // otra obra, el parámetro cambia y hay que refrescar (la página no se
    // desmonta porque es la misma ruta).
    const obraUrl = useMemo(() => new URLSearchParams(location.search).get('obra') || '', [location.search]);
    useEffect(() => {
        if (!puedeVer) return;
        setObra(obraUrl);
        setPagina(0);
        cargar({ obraOverride: obraUrl });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraUrl]);

    useEffect(() => {
        if (!puedeVer || !token) return undefined;
        const s = initiateSocketConnection();
        const onActualizada = () => cargarRef.current?.({ silencioso: true });
        s.on('alertas_obras_actualizadas', onActualizada);
        const id = setInterval(() => cargarRef.current?.({ silencioso: true }), 60000);
        return () => {
            s.off('alertas_obras_actualizadas', onActualizada);
            clearInterval(id);
        };
    }, [puedeVer, token]);

    const abrirModal = async (ventana) => {
        const hito = ventana.hitoPendiente;
        if (!hito) return;
        const esPv = hito.tipo === 'pv';
        setModal({ tipo: esPv ? 'pv' : 'notificacion', ventana, hito });
        setMensajeModal('');
        setDraft(null);
        setCargandoDraft(true);
        try {
            const res = await fetch(`${API}/alertas-obras/${ventana.id}/notificacion?tipo=${hito.tipo}`, { headers, cache: 'no-store' });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.message || 'No se pudo preparar el mail');
            setDraft({
                destinatarios: (json.data.destinatarios || []).join(', '),
                asunto: json.data.asunto || '',
                cuerpo: json.data.cuerpo || '',
                descripcion: '',
                nroPv: '',
            });
        } catch (e) {
            setMensajeModal(e.message);
        } finally {
            setCargandoDraft(false);
        }
    };

    const cerrarModal = () => {
        setModal(null);
        setDraft(null);
        setMensajeModal('');
    };

    const enviar = async () => {
        if (!modal || !draft) return;
        setEnviando(true);
        setMensajeModal('');
        try {
            const esPv = modal.tipo === 'pv';
            const url = esPv
                ? `${API}/alertas-obras/${modal.ventana.id}/resolver`
                : `${API}/alertas-obras/${modal.ventana.id}/notificacion`;
            const body = esPv
                ? {
                    nroPv: draft.nroPv,
                    descripcion: draft.descripcion,
                    email: {
                        destinatarios: draft.destinatarios,
                        asunto: draft.asunto,
                        cuerpo: draft.cuerpo,
                    },
                }
                : {
                    tipo: modal.hito.tipo,
                    destinatarios: draft.destinatarios,
                    asunto: draft.asunto,
                    cuerpo: draft.cuerpo,
                    descripcion: draft.descripcion,
                };
            const res = await fetch(url, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.message || 'No se pudo enviar');
            cerrarModal();
            await cargar({ silencioso: true });
        } catch (e) {
            setMensajeModal(e.message);
        } finally {
            setEnviando(false);
        }
    };

    const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

    const limpiarFiltros = () => {
        setSituacion('');
        setObra('');
        setPagina(0);
        cargar({ situacionOverride: '', obraOverride: '' });
    };

    if (!puedeVer) {
        return (
            <div className="p-6">
                <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Alertas de Obras</h1>
                <p className="mt-2 text-red-600">Acceso denegado.</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <h1 className="text-xl font-bold mb-1">Alertas de Obras</h1>
            <p className="text-sm text-gray-500 mb-4">
                Obras con anticipo pagado y sin acta de inicio de obra. Cada ciclo muestra su notificación con un color; solo el ciclo actual se resuelve y los futuros todavía no existen.
            </p>

            {/* Resumen (totales de todas las páginas) */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Obras</div>
                    <div className="text-2xl font-bold">{obrasTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Pendiente</div>
                    <div className="text-2xl font-bold text-amber-600">{pendientesTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">En Plazo</div>
                    <div className="text-2xl font-bold text-blue-600">{enPlazoTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Resueltas</div>
                    <div className="text-2xl font-bold text-green-600">{resueltasTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Sin notificación</div>
                    <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{sinNotificacionTotal}</div>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">Situación</label>
                    <select
                        value={situacion}
                        onChange={(e) => { setSituacion(e.target.value); setPagina(0); }}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
                    >
                        <option value="">Todas</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="en_plazo">En Plazo</option>
                        <option value="resuelta">Resuelta</option>
                        <option value="sin_notificacion">Sin notificación</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs text-gray-500 mb-1">Obra</label>
                    <div className="flex gap-1">
                        <input
                            value={obra}
                            onChange={(e) => setObra(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(0); cargar(); } }}
                            placeholder="025-002"
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-28"
                        />
                        <button
                            onClick={() => { setPagina(0); cargar(); }}
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            Buscar
                        </button>
                    </div>
                </div>

                <button
                    onClick={limpiarFiltros}
                    className="text-sm text-blue-600 hover:text-blue-800 underline pb-1"
                >
                    Limpiar filtros
                </button>
            </div>

            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
            {cargando && <div className="text-sm text-gray-500 dark:text-gray-400">Cargando…</div>}

            {!cargando && (
                <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-sm">
                        <thead className="bg-teal-700 text-white">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">Obra</th>
                                <th className="px-3 py-2 text-left font-medium">Ciclo</th>
                                <th className="px-3 py-2 text-left font-medium">Pago anticipo</th>
                                <th className="px-3 py-2 text-left font-medium">Vence</th>
                                <th className="px-3 py-2 text-left font-medium">Transcurridos</th>
                                <th className="px-3 py-2 text-left font-medium">Restantes</th>
                                <th className="px-3 py-2 text-left font-medium">Situación</th>
                                <th className="px-3 py-2 text-left font-medium">Hito actual</th>
                                <th className="px-3 py-2 text-left font-medium">PV</th>
                                <th className="px-3 py-2 text-left font-medium">Acción</th>
                                <th className="px-3 py-2 text-left font-medium">Eventos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ventanas.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                                        Sin alertas.
                                    </td>
                                </tr>
                            )}
                            {ventanas.map((v) => {
                                const hito = v.hitoActual;
                                const actual = v.cicloActual ?? v.ciclo;
                                // Los ciclos pasados y el actual se pueden cargar; los
                                // futuros no existen todavía. El actual se resalta.
                                const esActual = v.ciclo === actual;
                                const esFuturo = v.ciclo > actual;
                                const esPv = Number(v.ciclo) >= 3;
                                const puedeActuar = v.situacion === 'pendiente';
                                const notificada = v.estado === 'resuelta' && !v.nroPv
                                    && Array.isArray(v.eventos) && v.eventos.some((e) => e.tipo === 'notificacion');
                                const estadoLinea = ETIQUETA_SITUACION[v.situacion] || '—';
                                return (
                                    <Fragment key={v.id}>
                                        <tr className={`border-t border-gray-100 dark:border-gray-700 ${esFuturo ? 'opacity-40 bg-gray-50 dark:bg-gray-800/40' : esActual ? 'bg-teal-50 dark:bg-teal-900/20 border-2 border-teal-500' : ''}`}>
                                            <td
                                                className={`px-3 py-2 ${esFuturo ? 'cursor-default' : 'cursor-pointer'}`}
                                                title={esFuturo ? 'Ciclo todavía no alcanzado' : 'Doble click para abrir la obra'}
                                                onDoubleClick={esFuturo ? undefined : () => abrirObra(v.codigoObra)}
                                            >
                                                <div className="font-medium text-gray-800 dark:text-gray-100">{v.codigoObra}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{v.nombreObra || '—'}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full border ${colorBadge(v.color)}`}>
                                                    Ciclo {v.ciclo}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">{fechaDia(v.fechaBase)}</td>
                                            <td className="px-3 py-2">{fechaDia(v.fechaVencimiento)}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">{transcurridos(v.diasTranscurridos)}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {restantes(v.diasRestantes)}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={
                                                    v.situacion === 'pendiente'
                                                        ? 'text-amber-700 dark:text-amber-400'
                                                        : v.situacion === 'resuelta'
                                                            ? 'text-green-700 dark:text-green-400'
                                                            : 'text-gray-500 dark:text-gray-400'
                                                }>
                                                    {estadoLinea}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                {hito ? (
                                                    <div>
                                                        <div>{hito.label || ETIQUETA_HITO[hito.tipo] || hito.tipo}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">{fechaDia(hito.fecha)}</div>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                            <td className="px-3 py-2">{v.nroPv || '—'}</td>
                                            <td className="px-3 py-2">
                                                {puedeActuar ? (
                                                    <button
                                                        onClick={() => abrirModal(v)}
                                                        className="text-xs px-2 py-1 rounded-md bg-teal-700 text-white hover:bg-teal-800"
                                                    >
                                                        {esPv ? 'Confeccionar PV' : 'Enviar notificación'}
                                                    </button>
                                                ) : v.situacion === 'en_plazo' ? (
                                                    <button
                                                        type="button"
                                                        disabled
                                                        title="Todavía en plazo"
                                                        className="text-xs px-2 py-1 rounded-md bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed opacity-60"
                                                    >
                                                        {esPv ? 'Confeccionar PV' : 'Enviar notificación'}
                                                    </button>
                                                ) : notificada ? (
                                                    <span className="text-xs font-medium text-green-700 dark:text-green-400">notificación enviada</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <button
                                                    onClick={() => setAbierto(abierto === v.id ? null : v.id)}
                                                    disabled={esFuturo}
                                                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 disabled:text-gray-400 disabled:cursor-not-allowed"
                                                >
                                                    {abierto === v.id ? 'Ocultar' : `Ver (${v.eventos.length})`}
                                                </button>
                                            </td>
                                        </tr>
                                        {abierto === v.id && (
                                            <tr className="bg-gray-50 dark:bg-gray-700/40">
                                                <td colSpan={11} className="px-3 py-3">
                                                    {v.eventos.length === 0 && (
                                                        <div className="text-sm text-gray-400">Sin eventos</div>
                                                    )}
                                                    {v.eventos.map((ev) => (
                                                        <div key={ev.id} className="text-sm flex items-center gap-3 flex-wrap py-0.5">
                                                            <span className="text-gray-400">{fechaDia(ev.fecha)}</span>
                                                            <span className="font-medium">{ev.tipo}</span>
                                                            {ev.nro_pv && <span className="text-gray-600 dark:text-gray-300">PV: {ev.nro_pv}</span>}
                                                            {ev.descripcion && <span className="text-gray-600 dark:text-gray-300">{ev.descripcion}</span>}
                                                            {ev.creado_por && <span className="text-xs text-gray-400">por #{ev.creado_por}</span>}
                                                        </div>
                                                    ))}
                                                    {v.resueltoEn && (
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                            Resuelta {fecha(v.resueltoEn)}{v.resueltoPorNombre ? ` por ${v.resueltoPorNombre}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {paginas > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4 text-sm">
                    <button
                        onClick={() => setPagina((p) => Math.max(0, p - 1))}
                        disabled={pagina === 0}
                        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                    >
                        Anterior
                    </button>
                    <span className="text-gray-600 dark:text-gray-300">Página {pagina + 1} de {paginas}</span>
                    <button
                        onClick={() => setPagina((p) => Math.min(paginas - 1, p + 1))}
                        disabled={pagina >= paginas - 1}
                        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                    >
                        Siguiente
                    </button>
                </div>
            )}

            <Modal
                isOpen={Boolean(modal)}
                onClose={cerrarModal}
                title={
                    modal
                        ? `${modal.tipo === 'pv' ? 'Confeccionar PV' : 'Enviar notificación'} · ${modal.ventana.codigoObra}`
                        : ''
                }
                maxWidth="max-w-2xl"
            >
                {cargandoDraft && <p className="text-sm text-gray-500">Cargando el mail…</p>}
                {!cargandoDraft && draft && (
                    <div className="space-y-3">
                        {modal.tipo === 'pv' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nro de PV <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={draft.nroPv}
                                    onChange={(e) => setDraft((p) => ({ ...p, nroPv: e.target.value }))}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    placeholder="N° PV"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destinatarios</label>
                            <input
                                type="text"
                                value={draft.destinatarios}
                                onChange={(e) => setDraft((p) => ({ ...p, destinatarios: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder="mail@organizacion.org, otro@organizacion.org"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Separados por coma. Obligatorio al menos uno.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asunto</label>
                            <input
                                type="text"
                                value={draft.asunto}
                                onChange={(e) => setDraft((p) => ({ ...p, asunto: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cuerpo</label>
                            <textarea
                                rows={10}
                                value={draft.cuerpo}
                                onChange={(e) => setDraft((p) => ({ ...p, cuerpo: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Observación interna (opcional)
                            </label>
                            <textarea
                                rows={2}
                                value={draft.descripcion}
                                onChange={(e) => setDraft((p) => ({ ...p, descripcion: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        {mensajeModal && <p className="text-sm text-red-600">{mensajeModal}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={cerrarModal}
                                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={enviar}
                                disabled={enviando}
                                className="px-3 py-1.5 text-sm rounded-md bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50"
                            >
                                {enviando
                                    ? 'Enviando…'
                                    : modal.tipo === 'pv'
                                        ? 'Enviar PV y cerrar ciclo'
                                        : 'Enviar notificación'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
